import {el, inject} from "@dhx/ts-common/dom";
import { Combobox } from "@dhx/ts-combobox";
import { DataEvents } from "@dhx/ts-data";
import { IEventSystem } from "@dhx/ts-common/events";
import { GridEvents, ICol, IContentList, IRendererConfig, fixedRowContent } from "../types";

let inputDelay: any;
function onInput(eventSystem: IEventSystem<GridEvents>, colId: string, filter: fixedRowContent, filterObj: IContentList, e: any) {
	const inputHandler = () => {
		filterObj.value[colId] = e.target.value;
		eventSystem.fire(GridEvents.filterChange, [e.target.value, colId, filter]);
		eventSystem.fire(GridEvents.headerInput, [e.target.value, colId, filter]); // TODO: remove sute_7.0
	};
	if (filter === "selectFilter") {
		inputHandler();
		return;
	}
	if (inputDelay) {
		clearTimeout(inputDelay);
	}
	inputDelay = setTimeout(inputHandler, 250);
}

function applyMathMethod(column: ICol, config: IRendererConfig, method: any, validate?: any): any {
	const id = column.id;
	const columnData = validate ? validate(id, config.data) : config.data.reduce((items, item) => {
		if (item[id] !== undefined && item[id] !== "" && !isNaN(item[id])) {
			items.push(parseFloat(item[id]));
		}
		return items;
	}, []);

	// [todo] move to treegrid
	let roots = columnData;
	if (config.type === "tree") {
		roots = config.data.reduce((total, item) => {
			if (item.$level === 0) {
				if (item[id] !== undefined && item[id] !== "" && !isNaN(item[id])) {
					total.push((parseFloat(item[id]) || 0));
				} else {
					let value = 0;
					config.datacollection.eachChild(item.id, (cell) => {
						if (!config.datacollection.haveItems(cell.id)) {
							value += parseFloat(cell[id]);
						}
					});
					total.push(value);
				}
			}
			return total;
		}, []);
	}
	return method(columnData, roots);
}

export const content: IContentList = {
	inputFilter: {
		toHtml(column, config) {
			return el("label.dhx_grid-filter__label.dxi.dxi-magnify", [
				el("input", {
					type: "text",
					class: "dhx_input dhx_grid-filter",
					oninput: [onInput, config.events, column.id, "inputFilter", this],
					_key: column.id,
					value: this.value[column.id] || ""
				})
			]);
		},
		match: (value, match) => new RegExp(`${match}`, "i").test(value),
		value: {}
	},
	selectFilter: {
		toHtml(column, config) {
			return el("label.dhx_grid-filter__label.dxi.dxi-menu-down", [
				el("select.dxi.dxi-menu-down", {
					type: "text",
					class: "dhx_input dhx_grid-filter dhx_grid-filter--select",
					onchange: [onInput, config.events, column.id, "selectFilter", this],
					_key: column.id,
					value: this.value[column.id] || ""
				}, [
						el("option", { value: "" }, ""),
						...column.$uniqueData.map(val => val && el("option", { value: val }, val))
					])
			]);
		},
		match: (value, match) => value === match,
		value: {}
	},
	comboFilter: {
		toHtml(column, config) {
			let combo;
			const colId = column.id.toString();
			if(!this.combo[colId]){
				const conf = column.header.filter(item => item.filterConfig !== undefined)[0];
				if (conf && conf.filterConfig) {
					combo = new Combobox(null, JSON.parse(JSON.stringify(conf.filterConfig)));
				} else {
					combo = new Combobox(null, {});
				}
				combo.data.parse(column.$uniqueData.map(value => ({ value })));
				config.events.on(DataEvents.load, () => {
					combo.data.parse(column.$uniqueData.map(value => ({ value })));
				});
				this.combo[colId] = combo;
				combo.events.on("change", (id) => {
					if (id) {
						let item;
						let value;
						if (combo.data.getItem(id)) {
							item = combo.list.selection.getItem();
							value = item.value;
							config.events.fire(GridEvents.filterChange, [value, colId, "comboFilter"]);
							config.events.fire(GridEvents.headerInput, [value, colId, "comboFilter"]); // TODO: remove sute_7.0
						} else {
							config.events.fire(GridEvents.filterChange, ["", colId, "comboFilter"]);
							config.events.fire(GridEvents.headerInput, ["", colId, "comboFilter"]); // TODO: remove sute_7.0
						}
					}
				});
				combo.popup.events.on("afterHide", () => {
					if (!combo.list.selection.getItem()) {
						combo.clear();
						config.events.fire(GridEvents.filterChange, ["", colId, "comboFilter"]);
						config.events.fire(GridEvents.headerInput, ["", colId, "comboFilter"]); // TODO: remove sute_7.0
					}
				});
			} else {
				combo = this.combo[column.id];
			}
			return inject(combo.getRootView());
		},
		match: (value, match) => new RegExp(`${match}`, "i").test(value) && new RegExp(`${match}`, "i").exec(value).index === 0,
		destroy: () => {
			const comboFilters = this.content.comboFilter.combo;
			if (comboFilters) {
				for (const combo in comboFilters) {
					comboFilters[combo].destructor();
					delete comboFilters[combo];
				}
			}
		},
		value: {},
		combo: {}
	},
	sum: {
		calculate: (_col, roots) => roots.reduce((sum, c) => sum += parseFloat(c) || 0, 0).toFixed(3),
		toHtml(column, config) { return applyMathMethod(column, config, this.calculate); }
	},
	avg: {
		calculate: (_col, roots) => (roots.reduce((sum, c) => sum += c, 0) / _col.length).toFixed(3),
		toHtml(column, config) { return applyMathMethod(column, config, this.calculate); }
	},
	min: {
		calculate: col => Math.min(...col).toFixed(3),
		toHtml(column, config) { return applyMathMethod(column, config, this.calculate); }
	},
	max: {
		calculate: col => Math.max(...col).toFixed(3),
		toHtml(column, config) { return applyMathMethod(column, config, this.calculate); }
	},
	count: {
		calculate: (_col, roots) => {
			// [todo]
			return roots.reduce((count, c) => count += c, 0);
		},
		validate: (colId: string, data: any[]) => {
			return data.reduce((items, item) => {
				if (item[colId] !== undefined && item[colId] !== "") {
					if (isNaN(item)) {
						items.push(1);
					} else {
						items.push(item);
					}
				}
				return items;
			}, []);
		},
		toHtml(column, config) { return applyMathMethod(column, config, this.calculate, this.validate); }
	}
};